(() => {
  const DESKTOP_BREAKPOINT = 1100;

  const initHeader = (header) => {
    if (!header) return;

    /*
      Evita registrar listeners duplicados cuando Shopify
      vuelve a renderizar la sección en el editor.
    */
    if (header.dataset.zvInitialized === 'true') {
      return;
    }

    header.dataset.zvInitialized = 'true';

    const sectionWrapper = header.closest('.section-zv-shop-header');

    const menuToggle = header.querySelector('[data-zv-menu-toggle]');
    const drawer = header.querySelector('[data-zv-drawer]');

    const drawerCloseButtons = Array.from(
      header.querySelectorAll('[data-zv-drawer-close]')
    );

    const desktopDropdowns = Array.from(
      header.querySelectorAll('[data-zv-dropdown]')
    );

    const mobileDropdowns = Array.from(
      header.querySelectorAll('[data-zv-mobile-dropdown]')
    );


    /* ======================================================================
       HEADER FIJO
    ====================================================================== */

    const updateFixedHeaderHeight = () => {
      if (!sectionWrapper) return;

      const isFixed = sectionWrapper.classList.contains(
        'is-zv-header-fixed'
      );

      if (!isFixed) {
        document.body.style.removeProperty(
          '--zv-fixed-header-height'
        );

        return;
      }

      const headerHeight =
        sectionWrapper.getBoundingClientRect().height;

      document.body.style.setProperty(
        '--zv-fixed-header-height',
        `${Math.ceil(headerHeight)}px`
      );
    };


    const setupFixedHeader = () => {
      if (!sectionWrapper) return;

      const shouldBeFixed =
        header.classList.contains('zv-shop-header--sticky');

      sectionWrapper.classList.toggle(
        'is-zv-header-fixed',
        shouldBeFixed
      );

      document.body.classList.toggle(
        'zv-has-fixed-header',
        shouldBeFixed
      );

      if (shouldBeFixed) {
        window.requestAnimationFrame(
          updateFixedHeaderHeight
        );
      } else {
        document.body.style.removeProperty(
          '--zv-fixed-header-height'
        );
      }
    };


    setupFixedHeader();


    if ('ResizeObserver' in window && sectionWrapper) {
      const headerResizeObserver = new ResizeObserver(() => {
        updateFixedHeaderHeight();
      });

      headerResizeObserver.observe(sectionWrapper);
    }


    /* ======================================================================
       DROPDOWNS DESKTOP
    ====================================================================== */

    const closeDesktopDropdown = (dropdown) => {
      if (!dropdown) return;

      const toggle = dropdown.querySelector(
        '[data-zv-dropdown-toggle]'
      );

      dropdown.classList.remove('is-open');

      if (toggle) {
        toggle.setAttribute('aria-expanded', 'false');
      }
    };


    const closeAllDesktopDropdowns = (except = null) => {
      desktopDropdowns.forEach((dropdown) => {
        if (dropdown !== except) {
          closeDesktopDropdown(dropdown);
        }
      });
    };


    desktopDropdowns.forEach((dropdown) => {
      const toggle = dropdown.querySelector(
        '[data-zv-dropdown-toggle]'
      );

      if (!toggle) return;

      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        const willOpen =
          !dropdown.classList.contains('is-open');

        closeAllDesktopDropdowns(dropdown);

        dropdown.classList.toggle(
          'is-open',
          willOpen
        );

        toggle.setAttribute(
          'aria-expanded',
          String(willOpen)
        );
      });
    });


    /* ======================================================================
       CLIC FUERA DEL DROPDOWN
    ====================================================================== */

    const handleDocumentClick = (event) => {
      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      if (!header.contains(target)) {
        closeAllDesktopDropdowns();
        return;
      }

      const clickedDropdown = target.closest(
        '[data-zv-dropdown]'
      );

      if (!clickedDropdown) {
        closeAllDesktopDropdowns();
      }
    };

    document.addEventListener(
      'click',
      handleDocumentClick
    );


    /* ======================================================================
       DRAWER MÓVIL
    ====================================================================== */

    const openDrawer = () => {
      if (!drawer) return;

      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');

      document.body.classList.add(
        'zv-drawer-open'
      );

      if (menuToggle) {
        menuToggle.setAttribute(
          'aria-expanded',
          'true'
        );
      }

      closeAllDesktopDropdowns();

      const firstFocusable = drawer.querySelector(
        'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (firstFocusable) {
        window.requestAnimationFrame(() => {
          firstFocusable.focus();
        });
      }
    };


    const closeDrawer = ({
      restoreFocus = true
    } = {}) => {
      if (!drawer) return;

      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');

      document.body.classList.remove(
        'zv-drawer-open'
      );

      if (menuToggle) {
        menuToggle.setAttribute(
          'aria-expanded',
          'false'
        );

        if (restoreFocus) {
          menuToggle.focus();
        }
      }
    };


    if (menuToggle && drawer) {
      menuToggle.addEventListener('click', () => {
        const isOpen =
          drawer.classList.contains('is-open');

        if (isOpen) {
          closeDrawer();
        } else {
          openDrawer();
        }
      });
    }


    drawerCloseButtons.forEach((button) => {
      button.addEventListener('click', () => {
        closeDrawer();
      });
    });


    /* ======================================================================
       ACORDEONES MÓVILES
    ====================================================================== */

    const closeMobileDropdown = (group) => {
      if (!group) return;

      const toggle = group.querySelector(
        '[data-zv-mobile-dropdown-toggle]'
      );

      group.classList.remove('is-open');

      if (toggle) {
        toggle.setAttribute(
          'aria-expanded',
          'false'
        );
      }
    };


    mobileDropdowns.forEach((group) => {
      const toggle = group.querySelector(
        '[data-zv-mobile-dropdown-toggle]'
      );

      if (!toggle) return;

      toggle.addEventListener('click', () => {
        const willOpen =
          !group.classList.contains('is-open');

        group.classList.toggle(
          'is-open',
          willOpen
        );

        toggle.setAttribute(
          'aria-expanded',
          String(willOpen)
        );
      });
    });


    /* ======================================================================
       TECLADO
    ====================================================================== */

    const handleKeydown = (event) => {
      if (event.key !== 'Escape') {
        return;
      }

      closeAllDesktopDropdowns();

      mobileDropdowns.forEach((group) => {
        closeMobileDropdown(group);
      });

      if (
        drawer &&
        drawer.classList.contains('is-open')
      ) {
        closeDrawer();
      }
    };

    document.addEventListener(
      'keydown',
      handleKeydown
    );


    /* ======================================================================
       RESIZE
    ====================================================================== */

    let resizeFrame = null;

    const handleResize = () => {
      if (resizeFrame) {
        window.cancelAnimationFrame(
          resizeFrame
        );
      }

      resizeFrame = window.requestAnimationFrame(() => {
        setupFixedHeader();
        updateFixedHeaderHeight();

        if (
          window.innerWidth > DESKTOP_BREAKPOINT
        ) {
          if (
            drawer &&
            drawer.classList.contains('is-open')
          ) {
            closeDrawer({
              restoreFocus: false
            });
          }

          mobileDropdowns.forEach((group) => {
            closeMobileDropdown(group);
          });

        } else {
          closeAllDesktopDropdowns();
        }

        resizeFrame = null;
      });
    };

    window.addEventListener(
      'resize',
      handleResize
    );


    /* ======================================================================
       CARGA COMPLETA
    ====================================================================== */

    window.addEventListener(
      'load',
      updateFixedHeaderHeight
    );


    /* ======================================================================
       LINKS DEL DRAWER

       Cuando el usuario toca un enlace real,
       cerramos el drawer antes de navegar.
    ====================================================================== */

    if (drawer) {
      const drawerLinks = drawer.querySelectorAll(
        'a[href]'
      );

      drawerLinks.forEach((link) => {
        link.addEventListener('click', () => {
          closeDrawer({
            restoreFocus: false
          });
        });
      });
    }
  };


  /* ==========================================================================
     INICIALIZACIÓN
  ========================================================================== */

  const initAllHeaders = (root = document) => {
    const headers = root.querySelectorAll(
      '[data-zv-header]'
    );

    headers.forEach((header) => {
      initHeader(header);
    });
  };


  initAllHeaders();


  /* ==========================================================================
     SHOPIFY THEME EDITOR

     Shopify puede destruir y volver a crear una sección
     al modificarla desde Personalizar.
  ========================================================================== */

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initAllHeaders(event.target);

      window.requestAnimationFrame(() => {
        const header = event.target.querySelector(
          '[data-zv-header]'
        );

        if (!header) return;

        const sectionWrapper = header.closest(
          '.section-zv-shop-header'
        );

        if (!sectionWrapper) return;

        if (
          header.classList.contains(
            'zv-shop-header--sticky'
          )
        ) {
          sectionWrapper.classList.add(
            'is-zv-header-fixed'
          );

          document.body.classList.add(
            'zv-has-fixed-header'
          );

          const height =
            sectionWrapper.getBoundingClientRect().height;

          document.body.style.setProperty(
            '--zv-fixed-header-height',
            `${Math.ceil(height)}px`
          );
        }
      });
    }
  );


  document.addEventListener(
    'shopify:section:unload',
    () => {
      const remainingFixedHeader =
        document.querySelector(
          '.section-zv-shop-header.is-zv-header-fixed'
        );

      if (!remainingFixedHeader) {
        document.body.classList.remove(
          'zv-has-fixed-header'
        );

        document.body.style.removeProperty(
          '--zv-fixed-header-height'
        );

        document.body.classList.remove(
          'zv-drawer-open'
        );
      }
    }
  );
})();